// =============================================================================
// Pintravel — MUI Theme Configuration
// =============================================================================
// Maps the design tokens in `tokens.ts` onto MUI. Two schemes share one coral
// primary; only the neutral base flips between warm cream (light) and navy
// (dark).
//
// The design is deliberately near-flat: cards carry a 1px border and no shadow.
// Only the FAB, the floating search pill and the bottom sheet float — those
// shadows live in `brandShadows` and are applied per component, not via
// MUI elevation.
// =============================================================================

import { createTheme, type ThemeOptions } from '@mui/material/styles';

import { brandShadows, darkBrand, lightBrand, radii, type BrandTokens } from './tokens';

// -----------------------------------------------------------------------------
// Palette
// -----------------------------------------------------------------------------

const palette = {
  primary: {
    main: lightBrand.primary,
    light: '#F08A6E',
    dark: lightBrand.primaryDark,
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: lightBrand.navy,
    light: '#33436B',
    dark: '#141C33',
    contrastText: '#FFFFFF',
  },
  success: {
    main: lightBrand.teal,
    light: lightBrand.tealBright,
    dark: '#0A6B66',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: lightBrand.amber,
    light: '#F3C173',
    dark: lightBrand.amberText,
    contrastText: '#241E17',
  },
  error: {
    main: '#D9486B',
    light: '#E4738D',
    dark: '#B02F50',
    contrastText: '#FFFFFF',
  },
  info: {
    main: lightBrand.blue,
    light: '#5C8FE8',
    dark: '#1A4CA8',
    contrastText: '#FFFFFF',
  },
  background: {
    default: lightBrand.bg,
    paper: lightBrand.surface,
  },
  text: {
    primary: lightBrand.text,
    secondary: lightBrand.textMuted,
    disabled: lightBrand.iconMuted,
  },
  divider: lightBrand.border,
} as const;

const darkPalette = {
  primary: {
    main: darkBrand.primary,
    light: '#F08A6E',
    dark: darkBrand.primaryDark,
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#33436B',
    light: '#4A5C8A',
    dark: darkBrand.navy,
    contrastText: '#FFFFFF',
  },
  success: {
    main: darkBrand.teal,
    light: '#5AD6C8',
    dark: '#0E8C86',
    contrastText: '#0B1220',
  },
  warning: {
    main: darkBrand.amber,
    light: '#F3C173',
    dark: '#B07A18',
    contrastText: '#0B1220',
  },
  error: {
    main: '#E4738D',
    light: '#EF9AAE',
    dark: '#D9486B',
    contrastText: '#0B1220',
  },
  info: {
    main: '#5C8FE8',
    light: '#8AB1F0',
    dark: darkBrand.blue,
    contrastText: '#0B1220',
  },
  background: {
    default: darkBrand.bg,
    paper: darkBrand.surface,
  },
  text: {
    primary: darkBrand.text,
    secondary: darkBrand.textMuted,
    disabled: darkBrand.iconMuted,
  },
  divider: darkBrand.border,
} as const;

// -----------------------------------------------------------------------------
// Custom palette slot — screens read brand values via theme.palette.brand.*
// -----------------------------------------------------------------------------

declare module '@mui/material/styles' {
  interface Palette {
    brand: BrandTokens;
  }

  interface PaletteOptions {
    brand?: BrandTokens;
  }
}

// -----------------------------------------------------------------------------
// Typography — Poppins across the board
// -----------------------------------------------------------------------------
// Sizes come straight from the handoff table. Body is 14px (not the usual 16),
// which is why inputs override back up to 16px — anything smaller makes iOS
// Safari zoom on focus.

const fontFamily = [
  'Poppins',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
].join(',');

const typography: ThemeOptions['typography'] = {
  fontFamily,

  /** Onboarding hero — 44/800. */
  h1: {
    fontSize: '2.75rem',
    fontWeight: 800,
    lineHeight: 1.08,
    letterSpacing: '-0.01em',
  },
  /** Screen title / hero card title — 30/800. */
  h2: {
    fontSize: '1.875rem',
    fontWeight: 800,
    lineHeight: 1.15,
    letterSpacing: '-0.01em',
  },
  /** Screen title, standard — 28/800. */
  h3: {
    fontSize: '1.75rem',
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  /** Card title — 18/800. */
  h4: {
    fontSize: '1.125rem',
    fontWeight: 800,
    lineHeight: 1.3,
  },
  /** Bar title — 17/700. */
  h5: {
    fontSize: '1.0625rem',
    fontWeight: 700,
    lineHeight: 1.35,
  },
  /** List section title — 15/700. */
  h6: {
    fontSize: '0.9375rem',
    fontWeight: 700,
    lineHeight: 1.4,
    letterSpacing: 0,
    textTransform: 'none',
  },

  /** Body — 14/400, line-height 1.5. */
  body1: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.5,
  },
  /** Secondary body — 13/400. */
  body2: {
    fontSize: '0.8125rem',
    fontWeight: 400,
    lineHeight: 1.5,
  },

  /** Item title — 14/700. */
  subtitle1: {
    fontSize: '0.875rem',
    fontWeight: 700,
    lineHeight: 1.4,
  },
  /** Item subtitle — 13/600. */
  subtitle2: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },

  button: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.5,
    textTransform: 'none',
    letterSpacing: 0,
  },
  /** Meta — 12/500. Design floor for readable meta text is 11.5px. */
  caption: {
    fontSize: '0.75rem',
    fontWeight: 500,
    lineHeight: 1.45,
  },
  /** Badge label — 11.5/700, uppercase. */
  overline: {
    fontSize: '0.71875rem',
    fontWeight: 700,
    lineHeight: 1.4,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
};

// -----------------------------------------------------------------------------
// Shape
// -----------------------------------------------------------------------------

const shape = {
  borderRadius: radii.cardTight,
};

// -----------------------------------------------------------------------------
// Shadows — flattened
// -----------------------------------------------------------------------------
// Every MUI elevation resolves to 'none' so nothing picks up a stray shadow by
// default. The three floating elements apply `brandShadows` explicitly.

const shadows = Array.from({ length: 25 }, () => 'none') as ThemeOptions['shadows'];

// -----------------------------------------------------------------------------
// Component Overrides
// -----------------------------------------------------------------------------

function getComponentOverrides(mode: 'light' | 'dark'): ThemeOptions['components'] {
  const isLight = mode === 'light';
  const t = isLight ? lightBrand : darkBrand;

  return {
    // ---- Global ----
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          WebkitTextSizeAdjust: '100%',
        },
        body: {
          overscrollBehavior: 'none',
          backgroundColor: t.bg,
        },
        ':root': {
          '--safe-area-top': 'env(safe-area-inset-top, 0px)',
          '--safe-area-bottom': 'env(safe-area-inset-bottom, 0px)',
          '--safe-area-left': 'env(safe-area-inset-left, 0px)',
          '--safe-area-right': 'env(safe-area-inset-right, 0px)',
        },
      },
    },

    // ---- Buttons ----
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: radii.button,
          minHeight: 48,
          paddingLeft: 20,
          paddingRight: 20,
          fontWeight: 600,
          // Design spec: pressed = scale 0.97 + 8% darken, 150–200ms ease-out.
          transition: 'transform 160ms ease-out, background-color 160ms ease-out',
          '&:active': {
            transform: 'scale(0.97)',
          },
        },
        sizeSmall: {
          minHeight: 38,
          paddingLeft: 14,
          paddingRight: 14,
          fontSize: '0.8125rem',
          borderRadius: radii.tile,
        },
        sizeLarge: {
          minHeight: 54,
          paddingLeft: 26,
          paddingRight: 26,
          fontSize: '0.9375rem',
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: t.primaryDark,
          },
        },
        outlined: {
          borderColor: t.border,
          '&:hover': {
            borderColor: t.primary,
            backgroundColor: t.primaryTint,
          },
        },
        text: {
          '&:hover': {
            backgroundColor: t.primaryTint,
          },
        },
      },
    },

    // ---- Icon Buttons ----
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: 44,
          minHeight: 44,
          transition: 'transform 160ms ease-out',
          '&:active': {
            transform: 'scale(0.94)',
          },
        },
        sizeSmall: {
          minWidth: 36,
          minHeight: 36,
        },
      },
    },

    // ---- FAB ----
    MuiFab: {
      defaultProps: {
        color: 'primary',
      },
      styleOverrides: {
        root: {
          // Neutral lift rather than the coral glow from the spec — the glow
          // read as a halo bleeding onto the background.
          boxShadow: brandShadows.floating,
          width: 56,
          height: 56,
          transition: 'transform 160ms ease-out',
          '&:hover': {
            boxShadow: brandShadows.floating,
            backgroundColor: t.primaryDark,
          },
          '&:active': {
            transform: 'scale(0.95)',
          },
        },
      },
    },

    // ---- Cards ----
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: radii.card,
          border: `1px solid ${t.border}`,
          backgroundColor: t.surface,
          backgroundImage: 'none',
          boxShadow: 'none',
        },
      },
    },

    MuiCardActionArea: {
      styleOverrides: {
        root: {
          borderRadius: radii.card,
          transition: 'transform 160ms ease-out',
          '&:active': {
            transform: 'scale(0.985)',
          },
        },
      },
    },

    // ---- Paper ----
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: radii.card,
        },
      },
    },

    // ---- Inputs — pill shaped, label above the field ----
    // A pill radius and MUI's notched outline are incompatible: the notch is cut
    // 12px from the left, but a 999px radius means the straight top edge only
    // starts around 25px in, so the label lands outside the curve and the top
    // border visibly breaks. The design puts labels above the field anyway, so
    // the notch is switched off and the label is laid out as a normal block.
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        fullWidth: true,
        size: 'medium',
        // Keeps the label from animating down into the field.
        InputLabelProps: { shrink: true },
      },
    },

    MuiOutlinedInput: {
      defaultProps: {
        notched: false,
      },
      styleOverrides: {
        root: {
          paddingLeft: 14,
          borderRadius: radii.pill,
          minHeight: 50,
          backgroundColor: t.surface,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: t.border,
            top: 0,
            // No gap in the border — the label no longer sits on the outline.
            '& legend': {
              display: 'none',
            },
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: t.iconMuted,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: t.primary,
            borderWidth: 1.5,
          },
          // 16px keeps iOS Safari from zooming when an input takes focus.
          '& input': {
            fontSize: '1rem',
          },
        },
        multiline: {
          borderRadius: radii.card,
          paddingTop: 14,
          paddingBottom: 14,
        },
        input: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        outlined: {
          // FormControl is a flex column, so a statically positioned label
          // naturally stacks above the input.
          position: 'static',
          transform: 'none',
          maxWidth: '100%',
          marginBottom: 6,
          marginLeft: 4,
          fontSize: '0.8125rem',
          fontWeight: 600,
          color: t.textMuted,
          '&.Mui-focused': {
            color: t.textMuted,
          },
          '&.Mui-error': {
            color: undefined,
          },
        },
      },
    },

    // ---- Chips — the same corner as buttons and fields, not pills ----
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radii.chip,
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 30,
        },
        sizeSmall: {
          borderRadius: radii.chipSmall,
          height: 24,
          fontSize: '0.6875rem',
        },
        outlined: {
          borderColor: t.border,
        },
        filled: {
          backgroundColor: t.primaryTint,
          color: t.primary,
        },
        // MUI's own filledPrimary styles darken the background on hover and on
        // focus, all the way to the coral this override uses for the *text*.
        // A clickable chip keeps focus after a tap, so a selected filter sat
        // there as coral on coral — unreadable until the user clicked
        // somewhere else. Pinned to the tint in every state; focus is shown
        // with a ring instead, which cannot collide with the label.
        filledPrimary: {
          backgroundColor: t.primaryTint,
          color: t.primary,
          '&:hover, &:focus, &.Mui-focusVisible': {
            backgroundColor: t.primaryTint,
          },
          '&.Mui-focusVisible': {
            outline: `2px solid ${t.primary}`,
            outlineOffset: 2,
          },
        },
      },
    },

    // ---- App Bar ----
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        color: 'default',
      },
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${t.border}`,
          backgroundColor: t.surface,
          backgroundImage: 'none',
          boxShadow: 'none',
          paddingTop: 'var(--safe-area-top)',
        },
      },
    },

    // ---- Bottom Navigation ----
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          height: 52,
          borderTop: `1px solid ${t.border}`,
          backgroundColor: t.surfaceDeep,
        },
      },
    },

    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          minWidth: 64,
          paddingTop: 8,
          color: t.iconMuted,
          '&.Mui-selected': {
            paddingTop: 8,
            color: t.primary,
          },
        },
        label: {
          fontSize: '0.6875rem',
          fontWeight: 600,
          '&.Mui-selected': {
            fontSize: '0.6875rem',
            fontWeight: 700,
          },
        },
      },
    },

    // ---- Dialog / bottom sheet ----
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radii.hero,
          margin: 16,
          maxHeight: 'calc(100% - 32px)',
          border: `1px solid ${t.border}`,
        },
        paperFullScreen: {
          borderRadius: 0,
          margin: 0,
          maxHeight: '100%',
          border: 'none',
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: `${radii.hero}px ${radii.hero}px 0 0`,
          boxShadow: brandShadows.sheet,
          backgroundImage: 'none',
        },
        paperAnchorLeft: {
          borderRadius: `0 ${radii.hero}px ${radii.hero}px 0`,
        },
        paperAnchorRight: {
          borderRadius: `${radii.hero}px 0 0 ${radii.hero}px`,
        },
      },
    },

    // ---- Lists ----
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: radii.tile,
          minHeight: 48,
          '&:active': {
            backgroundColor: t.primaryTint,
          },
        },
      },
    },

    // ---- Tabs — coral underline ----
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 2.5,
          borderRadius: 2,
          backgroundColor: t.primary,
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '0.875rem',
          minHeight: 46,
          color: t.textMuted,
          '&.Mui-selected': {
            color: t.primary,
          },
        },
      },
    },

    // ---- Avatar ----
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          fontSize: '0.85rem',
          backgroundColor: t.navy,
          color: '#FFFFFF',
        },
      },
    },

    // ---- Skeleton ----
    MuiSkeleton: {
      defaultProps: {
        animation: 'wave',
      },
      styleOverrides: {
        root: {
          backgroundColor: isLight ? 'rgba(36,30,23,0.07)' : 'rgba(255,255,255,0.07)',
        },
        rounded: {
          borderRadius: radii.tile,
        },
      },
    },

    // ---- Tooltip ----
    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
      styleOverrides: {
        tooltip: {
          borderRadius: 10,
          fontSize: '0.75rem',
          fontWeight: 600,
          backgroundColor: t.navy,
        },
      },
    },

    // ---- Snackbar ----
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
        autoHideDuration: 4000,
      },
      styleOverrides: {
        root: {
          bottom: 'calc(80px + var(--safe-area-bottom))',
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: radii.tile,
          fontWeight: 500,
          fontSize: '0.8125rem',
        },
      },
    },

    // ---- Divider ----
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: t.separator,
        },
      },
    },

    // ---- Switch — 46×28 pill, coral when on ----
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 46,
          height: 28,
          padding: 0,
          display: 'flex',
        },
        switchBase: {
          padding: 3,
          '&.Mui-checked': {
            transform: 'translateX(18px)',
            color: '#FFFFFF',
            '& + .MuiSwitch-track': {
              backgroundColor: t.primary,
              opacity: 1,
            },
          },
        },
        thumb: {
          width: 22,
          height: 22,
          boxShadow: 'none',
        },
        track: {
          borderRadius: 999,
          backgroundColor: isLight ? '#DCD3C4' : '#2C3A61',
          opacity: 1,
        },
      },
    },

    // ---- Linear Progress — 8px track, coral fill ----
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          height: 8,
          backgroundColor: isLight ? t.border : 'rgba(255,255,255,0.15)',
        },
        bar: {
          borderRadius: 999,
          backgroundColor: t.primary,
        },
      },
    },
  };
}

// -----------------------------------------------------------------------------
// Breakpoints
// -----------------------------------------------------------------------------

const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
  },
};

// -----------------------------------------------------------------------------
// Transitions — design spec: 150–200ms ease-out
// -----------------------------------------------------------------------------

const transitions: ThemeOptions['transitions'] = {
  duration: {
    shortest: 150,
    shorter: 160,
    short: 200,
    standard: 250,
    complex: 320,
    enteringScreen: 200,
    leavingScreen: 170,
  },
};

// -----------------------------------------------------------------------------
// Z-Index layer map
// -----------------------------------------------------------------------------

const zIndex: ThemeOptions['zIndex'] = {
  mobileStepper: 1000,
  fab: 1050,
  speedDial: 1050,
  appBar: 1100,
  drawer: 1200,
  modal: 1300,
  snackbar: 1400,
  tooltip: 1500,
};

// -----------------------------------------------------------------------------
// Theme Factory
// -----------------------------------------------------------------------------

export function createAppTheme(mode: 'light' | 'dark') {
  const activePalette = mode === 'light' ? palette : darkPalette;

  return createTheme({
    palette: {
      mode,
      ...activePalette,
      brand: mode === 'light' ? lightBrand : darkBrand,
    },
    typography,
    shape,
    shadows,
    breakpoints,
    transitions,
    zIndex,
    components: getComponentOverrides(mode),
  });
}

// -----------------------------------------------------------------------------
// Default Exports
// -----------------------------------------------------------------------------

/** Light theme — "koral / zachód słońca". */
export const lightTheme = createAppTheme('light');

/** Dark theme — "nocny lot". */
export const darkTheme = createAppTheme('dark');

export default lightTheme;
